// The three role navs, and the route allow-list they double as.
//
// TES-1040 — moved out of AppShell so the nav (and with it the role gate) can
// be unit-tested with plain `node --test`: data only, relative imports, and
// the sidebar counts named by key (`NavBadge`) instead of computed here.
import type { ComponentType } from "react";
import {
  ArrowLeftRight,
  BarChart3,
  BellRing,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  CalendarRange,
  ChartPie,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  Contact,
  Droplets,
  FileCheck,
  FileText,
  Funnel,
  Globe,
  HelpCircle,
  MessageSquareText,
  History,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Megaphone,
  MessageSquare,
  MessageSquareReply,
  Milestone,
  Pill,
  Route as RouteIcon,
  Settings,
  Siren,
  Sprout,
  Stethoscope,
  Receipt,
  TestTube,
  UserPlus,
  UserRound,
  Users,
  Video,
  Workflow,
  Image as ImageIcon,
  ShieldCheck,
} from "lucide-react";
import type { EmployeeRole, WorkQueueLane } from "@care-by-ella/contracts";
import {
  LAB_RESULT_OPS_QUEUE_LABEL,
  SUBSCRIPTION_END_REFUND_QUEUE_LABEL,
} from "@care-by-ella/contracts";
// Type-only: a runtime import of a sibling module would need an extension
// that the Vite build refuses (test/nav-sections.test.ts pins the prefix
// against support-view's SUPPORT_VIEW_PREFIX instead).
import type { SupportView } from "./support-view";

/** Which count a nav entry carries; AppShell computes it from ClinicQueues. */
export type NavBadge =
  | { lane: WorkQueueLane }
  | "meetings"
  | "chat"
  | "transferReviews"
  | "workQueue"
  | "supportInbox";

export type NavIcon = ComponentType<{ size?: number; strokeWidth?: number }>;
export type NavSection = {
  label?: string;
  items: {
    to: string;
    label: string;
    icon: NavIcon;
    // Reachable by this role, but not offered as its own nav entry — a
    // sub-surface you arrive at from another screen. These lists double as the
    // route ALLOW-LIST (isPathAllowedForRole below), so dropping an item to
    // tidy the sidebar would silently revoke access to the route.
    hidden?: boolean;
    // TES-506 — how many items are waiting behind this entry. Reads the
    // shell's single shared fetch, so every badge in the sidebar and the Idag
    // dashboard are computed from one response. Absent = no badge (a surface
    // with nothing to count, like Patienter or Min profil).
    badge?: NavBadge;
  }[];
};

// TES-289 — the work-queue workspace entry. A doctor sees it labelled as their
// decision queue; a coach's whole workspace is built around it.
const WORK_QUEUE_ITEM = {
  to: "/work-queue",
  icon: ListChecks,
};

// TES-492 / TES-506 — the doctor nav: clinical work only.
//
// The list was 22 entries, 14 of them our own tooling (flow editor, journeys,
// import history, settings) — a doctor met our configuration surface before
// her patients. They moved to ADMIN_SECTIONS, which is a different person's
// job: Ö-57 settles that the doctor AUTHORS no content, so the four form
// templates belong to the admin.
//
// There were also three overlapping inboxes. The aggregate queue is gone as a
// doctor entry (the coach keeps it — see WORK_QUEUE_ITEM); every lane is its
// own entry carrying its own count, and /today ranks across them as the
// landing surface.
//
// /inbox survives at /health-declarations rather than becoming a lane page: it
// carries the TES-458 resa-filter and an assessment column the generic lane
// row has no room for. TES-1037 took it out of the visible nav (see the
// hidden entry under "Patienter"). "Paket att godkänna" is gone with content approval
// being paused.
const DOCTOR_SECTIONS: NavSection[] = [
  {
    label: "Mitt arbete",
    items: [
      { to: "/today", label: "Idag", icon: LayoutDashboard },
      {
        to: "/meetings",
        label: "Möten",
        icon: Video,
        badge: "meetings",
      },
      // TES-1233 — a passed meeting without documentation or active handling
      // (Emelie: "skapar en operativ uppgift i arbetskön"). Beside "Möten", not
      // under "Att besluta": a missed meeting is the doctor's own work today.
      {
        to: "/meetings-to-document",
        label: "Passerade möten",
        icon: CalendarX,
        badge: { lane: "meetings_to_document" },
      },
      {
        to: "/chat",
        label: "Chatt",
        icon: MessageSquare,
        badge: "chat",
      },
    ],
  },
  {
    label: "Att besluta",
    items: [
      // TES-986 — first: a RED case sits at the top of the doctor's work.
      {
        to: "/medical-reviews",
        label: "Medicinska ärenden",
        icon: Stethoscope,
        badge: { lane: "medical_reviews" },
      },
      {
        to: "/medical-alerts",
        label: "Medicinska larm",
        icon: Siren,
        badge: { lane: "medical_alerts" },
      },
      // TES-734 — A-02's own queue for the asynchronous transfer review.
      // TES-1032 — with the count of reviews waiting on the doctor.
      {
        to: "/receptovertag",
        label: "Receptövertag att granska",
        icon: ClipboardCheck,
        badge: "transferReviews",
      },
      {
        to: "/renewals",
        label: "Månadsincheckningar",
        icon: CalendarCheck,
        badge: { lane: "renewals" },
      },
      {
        to: "/side-effects",
        label: "Biverkningar",
        icon: Pill,
        badge: { lane: "side_effects" },
      },
      {
        to: "/handoffs",
        label: "Överlämningar",
        icon: ArrowLeftRight,
        badge: { lane: "handoffs" },
      },
      // TES-915 — the doctor's own "Följ upp senare" reminders once they are
      // due. The coach needs no entry: her stacked work queue shows every lane.
      {
        to: "/follow-ups",
        label: "Uppföljningar",
        icon: BellRing,
        badge: { lane: "follow_ups" },
      },
      // TES-519 — "Granskningskö" stood here. The queue is
      // gone; what stays has its own entry, its own badge, and lands on the
      // patient screen with the content up and one clear CTA.
      {
        to: "/checkins",
        label: "Incheckningar",
        icon: ClipboardCheck,
        badge: { lane: "checkins" },
      },
      {
        to: "/labs",
        label: "Blodprov",
        icon: Droplets,
        badge: { lane: "labs" },
      },
      // TES-442 — LabPortalen reports the sweep could not attach to an
      // order on its own. The doctor attaches or dismisses them.
      {
        to: "/lab-quarantine",
        label: "Provsvar utan koppling",
        icon: TestTube,
      },
      // TES-1059 — completion rounds whose every item is in.
      {
        to: "/completion-rounds",
        label: "Kompletteringar klara",
        icon: ListChecks,
        badge: { lane: "completion_rounds" },
      },
      // TES-1149 — her answers to the doctor's questions.
      {
        to: "/doctor-question-answers",
        label: "Svar på frågor",
        icon: MessageSquareReply,
        badge: { lane: "doctor_question_answers" },
      },
      // TES-765 (CP-05) — documented meetings not yet entered into Webdoc.
      // Last in the list: it blocks no care, but it must not be forgotten
      // either.
      {
        to: "/webdoc-confirmations",
        label: "Infört i Webdoc",
        icon: FileCheck,
        badge: { lane: "webdoc_confirmations" },
      },
    ],
  },
  {
    label: "Patienter",
    items: [
      { to: "/patients", label: "Patienter", icon: Users },
      { to: "/schedule", label: "Mitt schema", icon: CalendarDays },
      // TES-1292 — the medication catalogue: the doctor approves its entries.
      { to: "/medication-catalogue", label: "Läkemedelskatalog", icon: Pill },
      // The old landing route. Kept in the allow-list (it redirects to
      // /health-declarations) so a bookmark from before the split still opens
      // the page it used to, rather than the no-access state.
      { to: "/inbox", label: "Inkorg", icon: Inbox, hidden: true },
      // TES-1037 — out of the nav, and above all out of "Att besluta". Emelie:
      // "En inskickad hälsodeklaration är inte i sig en separat arbetsuppgift för Kirill."
      // Its count read as unsolved doctor tasks. The answers are read where
      // the work is: the patient overview, the meeting's health-declaration
      // panel and the transfer review each open the raw answers. So the list
      // stays only as a reachable route (hidden, no badge) for old links.
      {
        to: "/health-declarations",
        label: "Hälsodeklarationer",
        icon: ClipboardList,
        hidden: true,
      },
      // TES-497 — the clinician's own portrait. Their task, not our
      // configuration, so it stays in the doctor's nav through this split
      // (the coach nav already carries it). Deliberately NOT in
      // ADMIN_SECTIONS: employee-api/v1/media/portrait is
      // @Roles("doctor","coach"), so an admin link would only ever 403.
      { to: "/profile", label: "Min profil", icon: UserRound },
    ],
  },
];

// TES-491 — the admin nav. Everything that keeps the product running and
// nothing that treats a patient.
//
// Note what is NOT here: the inbox, the decision queue, meetings, patients,
// the doctor's and coach's chat. An administrator has no clinical role, so
// giving her a patient roster "just in case" would be a PDL problem, not a
// convenience. The Support inbox (TES-1102) lists only patients who are in a
// support conversation, and each thread opens under a support access reason.
const ADMIN_SECTIONS: NavSection[] = [
  {
    label: "Drift",
    items: [
      // TES-580 — the numbers, and the admin's landing page.
      { to: "/overview", label: "Översikt", icon: BarChart3 },
      // TES-1102 — the two-way Ella Support thread, answered by the admin
      // (base role or grant). The badge counts patient messages nobody on the
      // team has opened.
      {
        to: "/support-inbox",
        label: "Support",
        icon: LifeBuoy,
        badge: "supportInbox",
      },
      // TES-928 — cookieless visit statistics for the public web.
      { to: "/web-analytics", label: "Webbstatistik", icon: Globe },
      // TES-1076 — Emelie's ten onboarding steps, counts per day.
      { to: "/onboarding-funnel", label: "Onboardingtratt", icon: Funnel },
      // TES-1109 — the people with a verified e-mail, their furthest step and
      // their product-feedback consent. Names people, unlike the funnel:
      // logged as a read of each of them, like Bokningsdiagnostik.
      { to: "/onboarding-contacts", label: "Kontaktlista", icon: Contact },
      // TES-1676 — announcement campaigns: a push to a patient segment and
      // the Hem card behind it. Counts only, no names.
      { to: "/announcements", label: "Utskick", icon: Megaphone },
      // TES-1150 — the doctor's part is done, the subscription is not:
      // administrative follow-up and reminders. Names people: logged as a
      // read of each of them, like Kontaktlista.
      {
        to: "/awaiting-activation",
        label: "Inväntar abonnemangsaktivering",
        icon: Pill,
      },
      // TES-1102 S3 — who started booking and did not book, with the
      // contactable flag and the drop-off reason. Names people, unlike the
      // funnel: logged as a read of each of them.
      {
        to: "/booking-diagnostics",
        label: "Bokningsdiagnostik",
        icon: ClipboardList,
      },
      // TES-700 — the PDL access log becomes readable. It is written on every
      // patient read and has never had a surface; a log nobody reviews deters
      // nobody.
      { to: "/access-review", label: "Åtkomstgranskning", icon: ShieldCheck },
      // TES-442 — the LabPortalen sync and its quarantine, read-only for the
      // admin: she runs the integration and maps the unknown analyses (O7);
      // attaching or dismissing a report is the doctor's.
      { to: "/lab-quarantine", label: "LabPortalen", icon: TestTube },
      // TES-1401 (Ö-473) — the operational queue for a blood test whose
      // result has not come after the third check. Names people: logged as a
      // read of each of them, like Kontaktlista.
      {
        to: "/missing-lab-results",
        label: LAB_RESULT_OPS_QUEUE_LABEL,
        icon: TestTube,
      },
      // TES-1554 (Ö-863) — admin ends the subscription and refunds by hand
      // after the doctor's "not suitable" end. Names people: logged as a
      // read of each of them, like "Saknade provsvar".
      {
        to: "/subscription-end-refunds",
        label: SUBSCRIPTION_END_REFUND_QUEUE_LABEL,
        icon: Receipt,
      },
      // TES-1292 — the medication catalogue: links, strengths and intervals,
      // kept as versions by the admin.
      { to: "/medication-catalogue", label: "Läkemedelskatalog", icon: Pill },
      { to: "/staff", label: "Personal", icon: Users },
      // TES-756 — the doctors' bookable slots. Lives in ADMIN_SECTIONS because
      // the schedule is operations, not care: admin touches no patient data here,
      // she fills in which times a doctor can take meetings. The page shows a
      // picker for admin and the doctor's own week for a doctor, and the write
      // goes through the admin-gated clinicians/:employeeId/schedule.
      { to: "/schedule", label: "Läkarnas scheman", icon: CalendarDays },
      { to: "/import-history", label: "Importhistorik", icon: History },
      { to: "/settings", label: "Inställningar", icon: Settings },
      // The CAR-55 create form, reachable from the staff list. Kept as a route
      // (not a nav entry) so "Personal" is the one door to staff admin.
      {
        to: "/clinicians/new",
        label: "Ny personal",
        icon: UserPlus,
        hidden: true,
      },
    ],
  },
  {
    label: "Innehåll",
    items: [
      { to: "/articles", label: "Innehåll", icon: FileText },
      { to: "/media", label: "Bilder", icon: ImageIcon },
      { to: "/flows", label: "Flöden", icon: Workflow },
      { to: "/journeys", label: "Resor", icon: RouteIcon },
      { to: "/journey-phases", label: "Faser", icon: Milestone },
      { to: "/wellness", label: "Wellness Plan", icon: Sprout },
      { to: "/weekly-themes", label: "Veckotema", icon: CalendarRange },
      // TES-517 — "Coach-meddelanden" lived here. Ö-69, "I
      // don't want to see anything related to it in the admin ui at all."
      // The automatic send-out has been kill-switched since 2026-08-12; an editing
      // surface for something that never goes out was only confusion. Removed,
      // not hidden.
      { to: "/faq", label: "Frågor och svar", icon: HelpCircle },
      // TES-1355 — the welcome template the coach's welcome draft is built
      // from. Content, so the admin's; the coach only reads what is published.
      {
        to: "/coach-templates",
        label: "Coachmallar",
        icon: MessageSquareText,
      },
    ],
  },
  {
    label: "Formulär",
    items: [
      {
        to: "/intake-template",
        label: "Hälsodeklaration",
        icon: ClipboardList,
      },
      {
        to: "/weekly-checkin-template",
        label: "Veckans check-in",
        icon: CheckSquare,
      },
      // TES-371: milestone is an editable template kind.
      {
        to: "/milestone-template",
        label: "Månadsavstämning",
        icon: CalendarCheck,
      },
      // TES-456: F-07, the transfer paperwork, is an editable template kind.
      {
        to: "/prescription-transfer-template",
        label: "Receptövertag",
        icon: Pill,
      },
    ],
  },
];

// TES-493 — the coach nav, grouped by what a coach actually does: work the
// queue, talk to patients, then maintain the material they coach with.
//
// Three of these eight entries were dead on arrival before TES-493: /patients,
// /wellness and /weekly-themes all pointed at controllers declared
// @Roles("doctor"), so a coach clicking them got a 403 from a link their own
// sidebar offered. The API grants were widened rather than the links removed —
// the entries were put here deliberately in TES-289, so the 403 was the bug,
// not the menu.
const COACH_SECTIONS: NavSection[] = [
  {
    label: "Mitt arbete",
    items: [
      // The coach keeps the stacked queue: it IS her workspace, and four lanes
      // fit on one screen where the doctor's six did not.
      {
        ...WORK_QUEUE_ITEM,
        label: "Arbetskö",
        badge: "workQueue",
      },
      {
        to: "/chat",
        label: "Chatt",
        icon: MessageSquare,
        badge: "chat",
      },
      {
        to: "/checkins",
        // TES-1214 — A-01's section name, the same as the queue's lane.
        label: "Veckoincheckningar",
        icon: ClipboardCheck,
        badge: { lane: "checkins" },
      },
      {
        to: "/handoffs",
        label: "Överlämningar",
        icon: ArrowLeftRight,
        badge: { lane: "handoffs" },
      },
    ],
  },
  {
    label: "Patienter",
    items: [{ to: "/patients", label: "Patienter", icon: Users }],
  },
  {
    label: "Material",
    items: [
      // TES-517 — "Coach-meddelanden" borttagen, se ADMIN_SECTIONS.
      { to: "/wellness", label: "Wellness Plan", icon: Sprout },
      { to: "/weekly-themes", label: "Veckotema", icon: CalendarRange },
      // TES-497: the coach's own portrait (TES-719: her own key, care_team.employee.<id>).
      { to: "/profile", label: "Min profil", icon: UserRound },
    ],
  },
];

// TES-1040 — the admin's read-only copies of the doctor's and the coach's
// menus: "Läkarvy (läsläge)" and "Coachvy (läsläge)".
//
// Item for item the clinician's own menu, under the view's path prefix, minus
// what the API does not open to an admin:
//   - Chatt: chat is out of scope for the support views (TES-1040 spec);
//   - Mitt schema and Min profil: they are the signed-in clinician's own
//     schedule and portrait (`me/schedule`, `media/portrait`), and an admin
//     has neither; "Läkarnas scheman" in Drift is her schedule surface;
//   - the coach's Wellness Plan and Veckotema: the same editors the admin
//     already owns under "Innehåll", where she maintains them for real;
//   - Provsvar utan koppling (TES-442): the admin reads the same page as
//     "LabPortalen" under Drift.
//   - the medication catalogue (TES-1292): the admin maintains it under Drift.
// No counts: the badges are the clinician's to-do signal, and the admin is
// not the one the queue waits on.
const NOT_IN_SUPPORT_VIEW = new Set([
  "/chat",
  "/schedule",
  "/profile",
  "/wellness",
  "/weekly-themes",
  "/lab-quarantine",
  "/medication-catalogue",
]);

export const SUPPORT_SECTION_LABEL: Record<SupportView, string> = {
  doctor: "Läkarvy (läsläge)",
  coach: "Coachvy (läsläge)",
};

export function supportSection(
  view: SupportView,
  sections: NavSection[],
): NavSection {
  return {
    label: SUPPORT_SECTION_LABEL[view],
    items: sections
      .flatMap((s) => s.items)
      .filter((i) => !NOT_IN_SUPPORT_VIEW.has(i.to))
      .map((i) => ({
        to: `/support/${view}${i.to}`,
        label: i.label,
        icon: i.icon,
        ...(i.hidden ? { hidden: true } : {}),
      })),
  };
}

const ADMIN_WITH_SUPPORT_SECTIONS: NavSection[] = [
  ...ADMIN_SECTIONS,
  supportSection("doctor", DOCTOR_SECTIONS),
  supportSection("coach", COACH_SECTIONS),
];

// TES-1089 — the coach's sections, then the admin's. An entry both menus
// carry (Wellness Plan, Veckotema) stays once, in the coach's section, where
// she works with it daily.
const COACH_PATHS = new Set(
  COACH_SECTIONS.flatMap((s) => s.items).map((i) => i.to),
);
const COACH_WITH_ADMIN_SECTIONS: NavSection[] = [
  ...COACH_SECTIONS,
  ...ADMIN_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => !COACH_PATHS.has(i.to)),
  })).filter((s) => s.items.length > 0),
];

// TES-1326 — F-01 answers in groups, for quality and product follow-up.
// Aggregates only: no number opens a list of people, so it is not logged as a
// read of anyone (the API audits the view itself).
//
// TES-1372 — its own section, shown to whoever holds the "Insikter"
// permission whatever her role, and to nobody else (Ö-546).
export const INSIGHTS_SECTION: NavSection = {
  label: "Insikter",
  items: [{ to: "/f01-insights", label: "F-01-insikter", icon: ChartPie }],
};

// TES-489 — three navs for three jobs.
//
// This was `role === "coach" ? COACH : DOCTOR`, which meant an admin
// silently received the doctor's clinical workspace. An undefined role (claims not loaded yet, or a row whose role
// drifted outside the vocabulary) now gets the ADMIN nav rather than the
// doctor's: the admin surface holds no patient data, so an unknown principal
// falling into it discloses nothing.
//
// TES-1040 — only a signed-in admin gets the support views; an unknown role
// keeps the plain admin nav, which holds no patient data.
//
// TES-1089 — `roles` is every role the employee acts with (`/me` → roles):
// her base role plus grants. A coach with the admin grant (Emelie) gets her
// coach workspace followed by the admin's own sections, the statistics among
// them. Not the admin's read-only support views: she reads patients as their
// coach already, and a "Läkarvy" mixing the two would show her neither shape
// faithfully. Doctors never hold a grant.
export function sectionsForRole(
  role: EmployeeRole | undefined,
  roles?: readonly string[],
  permissions?: readonly string[],
): NavSection[] {
  const base = baseSectionsForRole(role, roles);
  return role && permissions?.includes("insights")
    ? [...base, INSIGHTS_SECTION]
    : base;
}

function baseSectionsForRole(
  role: EmployeeRole | undefined,
  roles?: readonly string[],
): NavSection[] {
  if (role === "coach" && roles?.includes("admin")) {
    return COACH_WITH_ADMIN_SECTIONS;
  }
  if (role === "coach") return COACH_SECTIONS;
  if (role === "doctor") return DOCTOR_SECTIONS;
  if (role === "admin") return ADMIN_WITH_SUPPORT_SECTIONS;
  return ADMIN_SECTIONS;
}

// TES-363 — the client-side route guard mirrors this nav filter: a role may
// open a path iff it (or its parent) is in the role's nav sections. Sub-paths
// (e.g. /patients/:id, /flows/:key) inherit their parent entry. "/" is the
// role-aware home redirect and is always allowed.
export function isPathAllowedForRole(
  role: EmployeeRole | undefined,
  pathname: string,
  roles?: readonly string[],
  permissions?: readonly string[],
): boolean {
  if (pathname === "/") return true;
  const allowed = sectionsForRole(role, roles, permissions)
    .flatMap((s) => s.items)
    .map((i) => i.to);
  return allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
